import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { DriversImportDialog } from "@/components/assignments/DriversImportDialog";

const Row = ({ d, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(d.name);
  const [phone, setPhone] = useState(d.phone || "");
  const save = async (extra = {}) => {
    try { await api.put(`/drivers/${d.id}`, { name, phone, active: d.active, ...extra }); setEdit(false); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Rimuovere ${d.name}?`)) return;
    try { const { data } = await api.delete(`/drivers/${d.id}`); toast.success(data.deactivated ? "Driver disattivato (ha assegnazioni nello storico)" : "Driver eliminato"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (edit) return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border p-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} className="min-w-[10rem] flex-1" data-testid={`driver-name-edit-${d.id}`} />
      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono" className="w-36" />
      <Button size="sm" onClick={() => save()} data-testid={`driver-save-${d.id}`}><Check className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" onClick={() => setEdit(false)}><X className="h-4 w-4" /></Button>
    </div>
  );
  return (
    <div className={`flex items-center gap-2 rounded-lg border p-2 ${d.active ? "" : "opacity-60"}`} data-testid={`driver-row-${d.id}`}>
      <div className="min-w-0 flex-1"><p className="truncate font-medium">{d.name}</p>{d.phone && <p className="text-xs text-slate-500">{d.phone}</p>}</div>
      {!d.active && <Button size="sm" variant="outline" onClick={() => save({ active: true })} data-testid={`driver-reactivate-${d.id}`}>Riattiva</Button>}
      <Button size="sm" variant="ghost" onClick={() => setEdit(true)} data-testid={`driver-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
      {d.active && <Button size="sm" variant="ghost" className="text-slate-500" onClick={remove} data-testid={`driver-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
    </div>
  );
};

export const DriversDialog = ({ open, stationId, drivers, onClose, onChanged }) => {
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [imp, setImp] = useState(false);
  const add = async () => {
    try { await api.post("/drivers", { name, station_id: stationId }); setName(""); toast.success("Driver aggiunto"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const list = drivers.filter((d) => d.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="drivers-dialog">
        <DialogHeader>
          <DialogTitle>Elenco driver della stazione</DialogTitle>
          <DialogDescription>{drivers.filter((d) => d.active).length} driver attivi. I nuovi nomi trovati nell'Excel vengono aggiunti in automatico.</DialogDescription>
        </DialogHeader>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e cognome" data-testid="driver-name-input" />
          <Button type="submit" disabled={name.trim().length < 2} data-testid="driver-add-button"><Plus className="mr-1 h-4 w-4" /> Aggiungi</Button>
        </form>
        <Button variant="outline" onClick={() => setImp(true)} data-testid="drivers-import-button"><Upload className="mr-2 h-4 w-4" /> Caricamento massivo (Excel o elenco)</Button>
        <DriversImportDialog key={String(imp)} open={imp} stationId={stationId} onClose={() => setImp(false)} onDone={() => { setImp(false); onChanged(); }} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca driver..." data-testid="driver-search-input" />
        <div className="space-y-2">
          {list.map((d) => <Row key={`${d.id}-${d.name}-${d.active}`} d={d} onChanged={onChanged} />)}
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Nessun driver.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
